/**
 * Load-once-and-reload, which is every read in this app.
 *
 * No cache and no polling. The findings queue is regenerated by a nightly sync,
 * so there is nothing to poll for, and a stale-while-revalidate layer would add
 * a class of bug — an operator acting on a cached queue after opening a claim —
 * for no benefit. Mutations call `reload` explicitly.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  readonly data: T | null;
  readonly error: unknown;
  readonly loading: boolean;
  readonly reload: () => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Holds the identity of the newest request. An older response that arrives
  // late must not overwrite a newer one — on the queue that would mean showing
  // findings that a just-completed action has already changed.
  const latest = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setLoading(true);

    loadRef
      .current()
      .then((result) => {
        if (latest.current !== ticket) return;
        setData(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (latest.current !== ticket) return;
        setError(caught);
      })
      .finally(() => {
        if (latest.current === ticket) setLoading(false);
      });
    // `load` is deliberately absent from the dependency list and read through a
    // ref instead: callers pass an inline closure, which is a new function on
    // every render, and depending on it would refetch in a loop. `deps` is what
    // the caller declares the request actually varies by.
  }, [nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}
